import { Component } from '@theme/component';

/**
 * "What's in the Box" inline reveal.
 *
 * Plays the product's contents/unboxing animation in place of the gallery: the
 * video crossfades over the main image (sized to the clip's own aspect ratio so
 * there are no letterbox bars) and expands into the faded-out thumbnail track
 * space. The clip plays once and freezes on its final frame; the viewer can
 * zoom in to inspect it and closes manually to return to the photos. A
 * `playing` / `zoomed` attribute pair on the host drives the transitions in
 * box-reveal.css.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The animation video.
 * @property {HTMLElement} player - The player overlay (aspect-ratio container).
 *
 * @extends {Component<Refs>}
 */
class BoxReveal extends Component {
  /** @type {string[]} */
  requiredRefs = ['video', 'player'];

  connectedCallback() {
    super.connectedCallback();
    const { video, player } = this.refs;
    // Match the overlay to the clip's intrinsic ratio → no black bars.
    video.addEventListener('loadedmetadata', this.#applyAspectRatio);
    // Pan the zoomed frame by following the pointer.
    player.addEventListener('pointermove', this.#handlePointerMove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.video?.removeEventListener('loadedmetadata', this.#applyAspectRatio);
    this.refs.player?.removeEventListener('pointermove', this.#handlePointerMove);
  }

  /** Reveals the player and starts the animation from the beginning. */
  open = () => {
    const { video } = this.refs;

    // Lazy-load the source the first time it's needed.
    if (!video.currentSrc && video.dataset.src) {
      video.src = video.dataset.src;
    }

    this.#resetZoom();
    this.setAttribute('playing', '');

    try {
      video.currentTime = 0;
    } catch {
      // currentTime may throw before metadata loads; safe to ignore.
    }

    const played = video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  };

  /** Restores the gallery. */
  close = () => {
    if (!this.hasAttribute('playing')) return;
    this.removeAttribute('playing');
    this.#resetZoom();
    // Pause once the fade-out has run so it doesn't freeze a frame on screen.
    this.refs.video.pause();
  };

  /**
   * Toggles the zoomed-in state (for inspecting the dense final frame).
   *
   * Disabled on mobile — the custom tap-to-zoom + pointer-pan is clumsy on
   * touch, so we hide the button there and let the phone's native pinch-to-zoom
   * (the viewport allows it) handle inspection instead.
   */
  zoomToggle = () => {
    if (!this.hasAttribute('playing')) return;
    if (!window.matchMedia('(min-width: 640px)').matches) return;
    if (!this.toggleAttribute('zoomed')) this.refs.video.style.transformOrigin = '';
  };

  #resetZoom() {
    this.removeAttribute('zoomed');
    this.refs.video.style.transformOrigin = '';
  }

  #applyAspectRatio = () => {
    const { video, player } = this.refs;
    if (video.videoWidth && video.videoHeight) {
      player.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.hasAttribute('zoomed')) return;
    const { video } = this.refs;
    const rect = video.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    video.style.transformOrigin = `${x}% ${y}%`;
  };
}

if (!customElements.get('box-reveal')) {
  customElements.define('box-reveal', BoxReveal);
}
